import { openDB, deleteDB, type DBSchema, type IDBPDatabase } from 'idb'
import type {
  GetContentBundleResponse,
  ApiNote,
  ApiBookmark,
  ApiCustomView,
  NoteChange,
  BookmarkChange,
  FeedbackSubmission,
} from '@/types/api'

const DB_NAME = 'cmmi-viewer'
const DB_VERSION = 7

interface ViewerDbSchema extends DBSchema {
  'content-bundle': {
    key: string
    value: {
      language: string
      contentHash: string
      bundle: GetContentBundleResponse
      cachedAt: number
    }
  }
  'media-cache': {
    key: string
    value: {
      key: string
      blob: Blob
      mimeType: string
      cachedAt: number
      sourceUrl: string
      // Legacy field from records written before alt text moved server-side; never written now.
      altText?: string | null
      contentHash?: string | null
      updatedOn?: string | null
    }
  }
  notes: {
    key: number
    value: ApiNote
  }
  bookmarks: {
    key: number
    value: ApiBookmark
  }
  'custom-views': {
    key: number
    value: ApiCustomView
  }
  'offline-queue': {
    key: number
    value: NoteChange
    autoIncrement: true
  }
  'bookmark-offline-queue': {
    key: number
    value: BookmarkChange
    autoIncrement: true
  }
  'feedback-offline-queue': {
    key: number
    value: FeedbackSubmission
    autoIncrement: true
  }
}

let dbPromise: Promise<IDBPDatabase<ViewerDbSchema>> | null = null
// Set while deleteViewerDb() is in flight. A connection opened during the delete
// blocks it forever, so getDb() waits for the delete to settle before reopening.
let deletePromise: Promise<void> | null = null

// An open request blocked by another tab's connection never settles on its own.
// Rejecting instead keeps callers (and the UI awaiting them) from hanging.
const DB_OPEN_TIMEOUT_MS = 10_000

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (err) => {
        clearTimeout(timer)
        reject(err)
      }
    )
  })
}

function openViewerDb(): Promise<IDBPDatabase<ViewerDbSchema>> {
  return openDB<ViewerDbSchema>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      if (!db.objectStoreNames.contains('content-bundle')) {
        db.createObjectStore('content-bundle', { keyPath: 'language' })
      }
      if (!db.objectStoreNames.contains('notes')) {
        db.createObjectStore('notes', { keyPath: 'id' })
      }
      if (!db.objectStoreNames.contains('bookmarks')) {
        db.createObjectStore('bookmarks', { keyPath: 'id' })
      }
      // Remove old saved-views store if upgrading from v1
      if (oldVersion < 2 && (db.objectStoreNames as DOMStringList).contains('saved-views')) {
        db.deleteObjectStore('saved-views' as Parameters<typeof db.deleteObjectStore>[0])
      }
      if (!db.objectStoreNames.contains('custom-views')) {
        db.createObjectStore('custom-views', { keyPath: 'id' })
      }
      if (!db.objectStoreNames.contains('offline-queue')) {
        db.createObjectStore('offline-queue', { autoIncrement: true })
      }
      if (!db.objectStoreNames.contains('bookmark-offline-queue')) {
        db.createObjectStore('bookmark-offline-queue', { autoIncrement: true })
      }
      if (oldVersion < 5) {
        db.createObjectStore('media-cache', { keyPath: 'key' })
      }
      if (!db.objectStoreNames.contains('feedback-offline-queue')) {
        db.createObjectStore('feedback-offline-queue', { autoIncrement: true })
      }
      if (oldVersion < 6) {
        if ((db.objectStoreNames as DOMStringList).contains('tour-steps')) {
          db.deleteObjectStore('tour-steps' as Parameters<typeof db.deleteObjectStore>[0])
        }
      }
    },
    blocked(currentVersion, blockedVersion) {
      console.warn(`[ViewerDb] open blocked (current v${currentVersion}, requested v${blockedVersion}): another tab holds a connection`)
    },
    // Another context is deleting or upgrading the database. Release this
    // connection immediately, otherwise that request blocks indefinitely.
    blocking() {
      void dbPromise?.then((db) => db.close()).catch(() => undefined)
      dbPromise = null
    },
    terminated() {
      dbPromise = null
    },
  })
}

export function getDb(): Promise<IDBPDatabase<ViewerDbSchema>> {
  if (!dbPromise) {
    const pending = withTimeout(
      (deletePromise ?? Promise.resolve()).then(() => openViewerDb()),
      DB_OPEN_TIMEOUT_MS,
      'IndexedDB open timed out: the database is blocked by another connection'
    )
    // A cached rejected promise would poison every later call; drop it so the next one retries.
    pending.catch(() => {
      if (dbPromise === pending) dbPromise = null
    })
    dbPromise = pending
  }
  return dbPromise
}

// Deletes the entire IndexedDB database, removing all cached site data.
export function deleteViewerDb(): Promise<void> {
  if (deletePromise) return deletePromise

  const run = async () => {
    if (dbPromise) {
      const db = await dbPromise.catch(() => null)
      db?.close()
      dbPromise = null
    }
    await deleteDB(DB_NAME, {
      blocked(currentVersion) {
        console.warn(`[ViewerDb] delete blocked (v${currentVersion}): another tab still holds a connection`)
      },
    })
  }

  const pending = run().finally(() => {
    if (deletePromise === pending) deletePromise = null
  })
  deletePromise = pending
  return pending
}

// Content Bundle
export async function saveContentBundle(bundle: GetContentBundleResponse): Promise<void> {
  const db = await getDb()
  await db.put('content-bundle', {
    language: bundle.language,
    contentHash: bundle.contentHash,
    bundle,
    cachedAt: Date.now(),
  })
}

export async function getCachedContentBundle(language: string): Promise<GetContentBundleResponse | null> {
  const db = await getDb()
  const entry = await db.get('content-bundle', language)
  return entry?.bundle ?? null
}

export async function getCachedContentHash(language: string): Promise<string | null> {
  const db = await getDb()
  const entry = await db.get('content-bundle', language)
  return entry?.contentHash ?? null
}

export async function clearContentBundle(language: string): Promise<void> {
  const db = await getDb()
  await db.delete('content-bundle', language)
}

export async function clearAllContentBundles(): Promise<void> {
  const db = await getDb()
  await db.clear('content-bundle')
}

// Notes
export async function saveAllNotes(notes: ApiNote[]): Promise<void> {
  const db = await getDb()
  const tx = db.transaction('notes', 'readwrite')
  await tx.store.clear()
  await Promise.all(notes.map((note) => tx.store.put(note)))
  await tx.done
}

export async function getAllNotes(): Promise<ApiNote[]> {
  const db = await getDb()
  return db.getAll('notes')
}

// Bookmarks
export async function saveAllBookmarks(bookmarks: ApiBookmark[]): Promise<void> {
  const db = await getDb()
  const tx = db.transaction('bookmarks', 'readwrite')
  await tx.store.clear()
  await Promise.all(bookmarks.map((bm) => tx.store.put(bm)))
  await tx.done
}

export async function getAllBookmarks(): Promise<ApiBookmark[]> {
  const db = await getDb()
  return db.getAll('bookmarks')
}

// Custom Views
export async function saveAllCustomViews(views: ApiCustomView[]): Promise<void> {
  const db = await getDb()
  const tx = db.transaction('custom-views', 'readwrite')
  await tx.store.clear()
  await Promise.all(views.map((v) => tx.store.put(v)))
  await tx.done
}

export async function getAllCustomViews(): Promise<ApiCustomView[]> {
  const db = await getDb()
  return db.getAll('custom-views')
}

// Offline Queue
export async function enqueueNoteChange(change: NoteChange): Promise<number> {
  const db = await getDb()
  return db.add('offline-queue', change) as Promise<number>
}

export async function getPendingNoteChanges(): Promise<Array<{ key: number; change: NoteChange }>> {
  const db = await getDb()
  const tx = db.transaction('offline-queue', 'readonly')
  const results: Array<{ key: number; change: NoteChange }> = []
  let cursor = await tx.store.openCursor()
  while (cursor) {
    results.push({ key: cursor.key as number, change: cursor.value })
    cursor = await cursor.continue()
  }
  await tx.done
  return results
}

export async function clearOfflineQueue(): Promise<void> {
  const db = await getDb()
  await db.clear('offline-queue')
}

// Bookmark Offline Queue
export async function enqueueBookmarkChange(change: BookmarkChange): Promise<number> {
  const db = await getDb()
  return db.add('bookmark-offline-queue', change) as Promise<number>
}

export async function getPendingBookmarkChanges(): Promise<Array<{ key: number; change: BookmarkChange }>> {
  const db = await getDb()
  const tx = db.transaction('bookmark-offline-queue', 'readonly')
  const results: Array<{ key: number; change: BookmarkChange }> = []
  let cursor = await tx.store.openCursor()
  while (cursor) {
    results.push({ key: cursor.key as number, change: cursor.value })
    cursor = await cursor.continue()
  }
  await tx.done
  return results
}

export async function clearBookmarkOfflineQueue(): Promise<void> {
  const db = await getDb()
  await db.clear('bookmark-offline-queue')
}

// Feedback Offline Queue
export async function enqueueFeedback(submission: FeedbackSubmission): Promise<number> {
  const db = await getDb()
  return db.add('feedback-offline-queue', submission) as Promise<number>
}

export async function getPendingFeedback(): Promise<Array<{ key: number; submission: FeedbackSubmission }>> {
  const db = await getDb()
  const tx = db.transaction('feedback-offline-queue', 'readonly')
  const results: Array<{ key: number; submission: FeedbackSubmission }> = []
  let cursor = await tx.store.openCursor()
  while (cursor) {
    results.push({ key: cursor.key as number, submission: cursor.value })
    cursor = await cursor.continue()
  }
  await tx.done
  return results
}

export async function deleteQueuedFeedback(keys: number[]): Promise<void> {
  if (keys.length === 0) return
  const db = await getDb()
  const tx = db.transaction('feedback-offline-queue', 'readwrite')
  await Promise.all(keys.map((key) => tx.store.delete(key)))
  await tx.done
}

export async function clearFeedbackOfflineQueue(): Promise<void> {
  const db = await getDb()
  await db.clear('feedback-offline-queue')
}

// Media Cache

export interface MediaCacheVersion {
  contentHash?: string | null
  updatedOn?: string | null
}

export async function getMediaBlob(key: string): Promise<{ blob: Blob; mimeType: string } | null> {
  const db = await getDb()
  const entry = await db.get('media-cache', key)
  if (!entry) return null
  return { blob: entry.blob, mimeType: entry.mimeType }
}

export async function getMediaCacheVersion(key: string): Promise<MediaCacheVersion | null> {
  const db = await getDb()
  const entry = await db.get('media-cache', key)
  if (!entry) return null
  return { contentHash: entry.contentHash ?? null, updatedOn: entry.updatedOn ?? null }
}

export async function saveMediaBlob(
  key: string,
  blob: Blob,
  mimeType: string,
  sourceUrl: string,
  version?: MediaCacheVersion,
): Promise<void> {
  const db = await getDb()
  await db.put('media-cache', {
    key,
    blob,
    mimeType,
    cachedAt: Date.now(),
    sourceUrl,
    contentHash: version?.contentHash ?? null,
    updatedOn: version?.updatedOn ?? null,
  })
}

export async function hasMediaBlob(key: string): Promise<boolean> {
  const db = await getDb()
  const key_ = await db.getKey('media-cache', key)
  return key_ !== undefined
}

export async function getAllMediaKeys(): Promise<string[]> {
  const db = await getDb()
  return db.getAllKeys('media-cache')
}

/**
 * Union of media keys (system file names and external URLs) referenced by every cached
 * content bundle. Returns null when any cached bundle predates the bundle media list, since
 * its references are unknown and evicting against an incomplete set would drop its media.
 */
export async function getAllCachedBundleMediaKeys(): Promise<Set<string> | null> {
  const db = await getDb()
  const entries = await db.getAll('content-bundle')
  const keys = new Set<string>()
  for (const { bundle } of entries) {
    if (!bundle.media) return null
    for (const asset of bundle.media) keys.add(asset.systemFileName)
    for (const url of bundle.externalMedia ?? []) keys.add(url)
  }
  return keys
}

export async function deleteMediaBlob(key: string): Promise<void> {
  const db = await getDb()
  await db.delete('media-cache', key)
}

export async function clearMediaCache(): Promise<void> {
  const db = await getDb()
  await db.clear('media-cache')
}
